import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Heart, Activity, Thermometer, Scale, Wind, Droplets,
  TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle,
  RefreshCw, Calendar, Clock
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Vitals = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [todayLogged, setTodayLogged] = useState(false);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [latestVitals, setLatestVitals] = useState(null);
  
  const [formData, setFormData] = useState({
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    heart_rate: '',
    weight: '',
    weight_unit: 'kg',
    temperature: '',
    temperature_unit: 'C',
    oxygen_saturation: '',
    respiratory_rate: '',
    notes: ''
  });

  useEffect(() => {
    fetchTodayVitals();
    fetchHistory();
    fetchSummary();
    fetchLatestVitals();
  }, []);

  const fetchTodayVitals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vitals/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.has_logged_today) {
        setTodayLogged(true);
        const vitals = response.data.vitals;
        setFormData({
          blood_pressure_systolic: vitals.blood_pressure_systolic || '',
          blood_pressure_diastolic: vitals.blood_pressure_diastolic || '',
          heart_rate: vitals.heart_rate || '',
          weight: vitals.weight || '',
          weight_unit: vitals.weight_unit || 'kg',
          temperature: vitals.temperature || '',
          temperature_unit: vitals.temperature_unit || 'C',
          oxygen_saturation: vitals.oxygen_saturation || '',
          respiratory_rate: vitals.respiratory_rate || '',
          notes: vitals.notes || ''
        });
      }
    } catch (error) {
      console.error('Error fetching today vitals:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vitals?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.vitals || []);
    } catch (error) {
      console.error('Error fetching vitals history:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vitals/summary?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchLatestVitals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vitals/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLatestVitals(response.data.vitals);
    } catch (error) {
      console.error('Error fetching latest vitals:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    // Validate at least one vital is entered
    const hasData = formData.blood_pressure_systolic || formData.heart_rate || 
                   formData.weight || formData.temperature || formData.oxygen_saturation;
    
    if (!hasData) {
      toast({
        title: "Enter vitals",
        description: "Please enter at least one vital measurement",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== null) {
          if (['blood_pressure_systolic', 'blood_pressure_diastolic', 'heart_rate', 
               'weight', 'temperature', 'oxygen_saturation', 'respiratory_rate'].includes(key)) {
            dataToSend[key] = parseFloat(value);
          } else {
            dataToSend[key] = value;
          }
        }
      });

      const response = await axios.post(`${API_URL}/api/vitals`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.warnings && response.data.warnings.length > 0) {
        toast({
          title: "⚠️ Health Alert",
          description: response.data.warnings.join('. '),
          variant: "destructive"
        });
      } else {
        toast({
          title: "Vitals logged! ✅",
          description: "Your vital signs have been recorded.",
        });
      }
      
      setTodayLogged(true);
      fetchHistory();
      fetchSummary();
      fetchLatestVitals();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log vitals. Please try again.",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  const getBloodPressureStatus = (systolic, diastolic) => {
    if (!systolic || !diastolic) return null;
    if (systolic < 90 || diastolic < 60) return { status: 'low', color: 'text-blue-600', label: 'Low' };
    if (systolic < 120 && diastolic < 80) return { status: 'normal', color: 'text-green-600', label: 'Normal' };
    if (systolic < 140 || diastolic < 90) return { status: 'elevated', color: 'text-yellow-600', label: 'Elevated' };
    return { status: 'high', color: 'text-red-600', label: 'High' };
  };

  const getHeartRateStatus = (hr) => {
    if (!hr) return null;
    if (hr < 60) return { status: 'low', color: 'text-blue-600', label: 'Low' };
    if (hr <= 100) return { status: 'normal', color: 'text-green-600', label: 'Normal' };
    return { status: 'high', color: 'text-red-600', label: 'Elevated' };
  };

  const getOxygenStatus = (spo2) => {
    if (!spo2) return null;
    if (spo2 >= 95) return { status: 'normal', color: 'text-green-600', label: 'Normal' };
    if (spo2 >= 90) return { status: 'low', color: 'text-yellow-600', label: 'Low' };
    return { status: 'critical', color: 'text-red-600', label: 'Critical' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 via-rose-500 to-pink-500 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <Activity className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-3xl font-bold">Daily Vitals Check</h2>
            <p className="text-red-100 text-lg">Track your vital signs for better health insights</p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      {latestVitals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <Heart className="w-8 h-8 mx-auto text-red-500 mb-2" />
              <p className="text-2xl font-bold">
                {latestVitals.blood_pressure_systolic || '--'}/{latestVitals.blood_pressure_diastolic || '--'}
              </p>
              <p className="text-xs text-gray-500">Blood Pressure</p>
              {latestVitals.blood_pressure_systolic && (
                <Badge className={`mt-1 ${getBloodPressureStatus(latestVitals.blood_pressure_systolic, latestVitals.blood_pressure_diastolic)?.color}`} variant="outline">
                  {getBloodPressureStatus(latestVitals.blood_pressure_systolic, latestVitals.blood_pressure_diastolic)?.label}
                </Badge>
              )}
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <Activity className="w-8 h-8 mx-auto text-rose-500 mb-2" />
              <p className="text-2xl font-bold">{latestVitals.heart_rate || '--'}</p>
              <p className="text-xs text-gray-500">Heart Rate (bpm)</p>
              {latestVitals.heart_rate && (
                <Badge className={`mt-1 ${getHeartRateStatus(latestVitals.heart_rate)?.color}`} variant="outline">
                  {getHeartRateStatus(latestVitals.heart_rate)?.label}
                </Badge>
              )}
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <Droplets className="w-8 h-8 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold">{latestVitals.oxygen_saturation || '--'}%</p>
              <p className="text-xs text-gray-500">SpO2</p>
              {latestVitals.oxygen_saturation && (
                <Badge className={`mt-1 ${getOxygenStatus(latestVitals.oxygen_saturation)?.color}`} variant="outline">
                  {getOxygenStatus(latestVitals.oxygen_saturation)?.label}
                </Badge>
              )}
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <Scale className="w-8 h-8 mx-auto text-purple-500 mb-2" />
              <p className="text-2xl font-bold">
                {latestVitals.weight || '--'} {latestVitals.weight ? latestVitals.weight_unit : ''}
              </p>
              <p className="text-xs text-gray-500">Weight</p>
              {summary?.weight_trend && (
                <Badge variant="outline" className="mt-1">
                  {summary.weight_trend === 'decreasing' ? '↓' : summary.weight_trend === 'increasing' ? '↑' : '→'} {summary.weight_trend}
                </Badge>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Thermometer className="w-5 h-5 text-red-500" />
                Log Your Vitals
              </CardTitle>
              <CardDescription>
                {todayLogged ? "Update today's vitals" : "Enter your vital measurements"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Blood Pressure */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="blood_pressure_systolic" className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-red-500" />
                    Systolic (mmHg)
                  </Label>
                  <Input
                    id="blood_pressure_systolic"
                    name="blood_pressure_systolic"
                    type="number"
                    placeholder="120"
                    value={formData.blood_pressure_systolic}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="blood_pressure_diastolic">Diastolic (mmHg)</Label>
                  <Input
                    id="blood_pressure_diastolic"
                    name="blood_pressure_diastolic"
                    type="number"
                    placeholder="80"
                    value={formData.blood_pressure_diastolic}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Heart Rate & SpO2 */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="heart_rate" className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-rose-500" />
                    Heart Rate (bpm)
                  </Label>
                  <Input
                    id="heart_rate"
                    name="heart_rate"
                    type="number"
                    placeholder="72"
                    value={formData.heart_rate}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="oxygen_saturation" className="flex items-center gap-2">
                    <Droplets className="w-4 h-4 text-blue-500" />
                    Oxygen Saturation (%)
                  </Label>
                  <Input
                    id="oxygen_saturation"
                    name="oxygen_saturation"
                    type="number"
                    placeholder="98"
                    value={formData.oxygen_saturation}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Weight & Temperature */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="weight" className="flex items-center gap-2">
                    <Scale className="w-4 h-4 text-purple-500" />
                    Weight
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="weight"
                      name="weight"
                      type="number"
                      step="0.1"
                      placeholder="70"
                      value={formData.weight}
                      onChange={handleChange}
                      className="flex-1"
                    />
                    <select
                      name="weight_unit"
                      value={formData.weight_unit}
                      onChange={handleChange}
                      className="px-3 py-2 border rounded-md bg-white dark:bg-gray-800"
                    >
                      <option value="kg">kg</option>
                      <option value="lbs">lbs</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="temperature" className="flex items-center gap-2">
                    <Thermometer className="w-4 h-4 text-orange-500" />
                    Temperature
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="temperature"
                      name="temperature"
                      type="number"
                      step="0.1"
                      placeholder="36.5"
                      value={formData.temperature}
                      onChange={handleChange}
                      className="flex-1"
                    />
                    <select
                      name="temperature_unit"
                      value={formData.temperature_unit}
                      onChange={handleChange}
                      className="px-3 py-2 border rounded-md bg-white dark:bg-gray-800"
                    >
                      <option value="C">°C</option>
                      <option value="F">°F</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Respiratory Rate */}
              <div>
                <Label htmlFor="respiratory_rate" className="flex items-center gap-2">
                  <Wind className="w-4 h-4 text-teal-500" />
                  Respiratory Rate (breaths/min)
                </Label>
                <Input
                  id="respiratory_rate"
                  name="respiratory_rate"
                  type="number"
                  placeholder="16"
                  value={formData.respiratory_rate}
                  onChange={handleChange}
                  className="mt-1 max-w-xs"
                />
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <Input
                  id="notes"
                  name="notes"
                  placeholder="Any additional observations..."
                  value={formData.notes}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {todayLogged ? 'Update Vitals' : 'Save Vitals'}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - History */}
        <div className="space-y-6">
          {/* Summary Card */}
          {summary && (
            <Card className="shadow-lg border-0 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-red-500" />
                  30-Day Averages
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {summary.avg_blood_pressure_systolic && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Blood Pressure</span>
                    <span className="font-semibold">
                      {summary.avg_blood_pressure_systolic}/{summary.avg_blood_pressure_diastolic} mmHg
                    </span>
                  </div>
                )}
                {summary.avg_heart_rate && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Heart Rate</span>
                    <span className="font-semibold">{summary.avg_heart_rate} bpm</span>
                  </div>
                )}
                {summary.avg_oxygen_saturation && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">SpO2</span>
                    <span className="font-semibold">{summary.avg_oxygen_saturation}%</span>
                  </div>
                )}
                {summary.latest_weight && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Latest Weight</span>
                    <span className="font-semibold">{summary.latest_weight} kg</span>
                  </div>
                )}
                <p className="text-xs text-center text-gray-500 pt-2">
                  Based on {summary.total_entries} entries
                </p>
              </CardContent>
            </Card>
          )}

          {/* Recent History */}
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Recent Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {history.slice(0, 7).map((entry) => (
                    <div 
                      key={entry.id} 
                      className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-gray-500">
                          {new Date(entry.logged_at).toLocaleDateString()}
                        </span>
                        <Clock className="w-3 h-3 text-gray-400" />
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {entry.blood_pressure_systolic && (
                          <div>
                            <span className="text-gray-500">BP:</span>{' '}
                            <span className="font-medium">{entry.blood_pressure_systolic}/{entry.blood_pressure_diastolic}</span>
                          </div>
                        )}
                        {entry.heart_rate && (
                          <div>
                            <span className="text-gray-500">HR:</span>{' '}
                            <span className="font-medium">{entry.heart_rate} bpm</span>
                          </div>
                        )}
                        {entry.oxygen_saturation && (
                          <div>
                            <span className="text-gray-500">SpO2:</span>{' '}
                            <span className="font-medium">{entry.oxygen_saturation}%</span>
                          </div>
                        )}
                        {entry.weight && (
                          <div>
                            <span className="text-gray-500">Weight:</span>{' '}
                            <span className="font-medium">{entry.weight} {entry.weight_unit}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">No vitals logged yet. Start tracking!</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Vitals;
